import {
	createFileRoute,
	useNavigate,
	useParams,
	useRouteContext,
} from "@tanstack/react-router";
import { useState } from "react";
import {
	graphql,
	useFragment,
	useLazyLoadQuery,
	useMutation,
} from "react-relay";
import { toast } from "sonner";
import { CourseForm, type CourseFormValues } from "@/forms/course-form";
import { useFileUpload } from "@/hooks/useFileUpload";
import type { cursosCriar_club$key } from "./__generated__/cursosCriar_club.graphql";
import type { cursosCriarCreateCourseMutation } from "./__generated__/cursosCriarCreateCourseMutation.graphql";
import type { cursosCriarQuery } from "./__generated__/cursosCriarQuery.graphql";

export const Route = createFileRoute(
	"/_logged/c/$club/(sidebar)/(admins)/area-de-membros/cursos/$category/criar",
)({
	component: RouteComponent,
});

function RouteComponent() {
	const navigate = useNavigate({ from: Route.fullPath });
	const { club: clubSlug, category } = useParams({ from: Route.id });
	const { session } = useRouteContext({ from: "__root__" });

	const [isSubmitting, setIsSubmitting] = useState(false);

	const queryData = useLazyLoadQuery<cursosCriarQuery>(
		graphql`
			query cursosCriarQuery($clubSlug: String!) {
				club(slug: $clubSlug) {
					...cursosCriar_club
					coursePage {
						categories(status: ALL) {
							id
							orientation
						}
					}
				}
			}
		`,
		{ clubSlug },
	);

	const clubData = useFragment<cursosCriar_club$key>(
		graphql`
			fragment cursosCriar_club on Club {
				subscription {
					features {
						key
						value
					}
				}
				offers(first: 100) {
					edges {
						node {
							id
							productName
							checkoutUrl
						}
					}
				}
				...upgradePlanCard_clubFragment
			}
		`,
		queryData.club,
	);

	const [createCourse] = useMutation<cursosCriarCreateCourseMutation>(graphql`
		mutation cursosCriarCreateCourseMutation(
			$club: String!
			$payload: CreateCourseInput!
		) {
			createCourse(club: $club, payload: $payload) {
				node {
					id
					slug
				}
			}
		}
	`);

	const { upload } = useFileUpload({ club: clubSlug, session });

	const secretVisibilityAllowed =
		clubData?.subscription?.features?.find(
			(feat) => feat.key === "secret_courses",
		)?.value === "1" || false;

	const availableOffers =
		clubData.offers.edges.map((e) => ({
			id: e.node.id,
			productName: e.node.productName ?? null,
			checkoutUrl: e.node.checkoutUrl ?? null,
		})) ?? [];

	const categoryData = queryData.club.coursePage.categories.find(
		(c) => c.id === category,
	);
	const orientation =
		(categoryData?.orientation as "LANDSCAPE" | "PORTRAIT" | null) ?? null;

	const handleSubmit = async (values: CourseFormValues) => {
		setIsSubmitting(true);
		return new Promise<void>((resolve, reject) => {
			createCourse({
				variables: {
					club: clubSlug,
					payload: {
						name: values.name,
						description: values.description,
						visibility: values.visibility,
						lessonAccessControl: values.classAccessControl,
						category,
						landscapeImageFile: values.landscapeImage?.id ?? null,
						portraitImageFile: values.portraitImage?.id ?? null,
						externalProducts: values.externalProducts,
						primaryProduct: values.primaryProduct || null,
					},
				},
				onCompleted: (response, errors) => {
					if (errors?.length) {
						toast.error(errors[0].message);
						setIsSubmitting(false);
						reject(new Error(errors[0].message));
						return;
					}
					toast.success("Curso criado com sucesso!");
					setIsSubmitting(false);
					navigate({
						to: "/c/$club/area-de-membros/cursos/$slug",
						params: {
							club: clubSlug,
							slug: response.createCourse.node.slug,
						},
					});
					resolve();
				},
				onError: (error) => {
					toast.error("Erro ao criar curso. Tente novamente.");
					setIsSubmitting(false);
					reject(error);
				},
			});
		});
	};

	return (
		<div className="p-6">
			<CourseForm
				mode="create"
				onSubmit={handleSubmit}
				offers={availableOffers}
				orientation={orientation}
				onUpload={upload}
				club={clubSlug}
				isSubmitting={isSubmitting}
				secretVisibilityAllowed={secretVisibilityAllowed}
				upgradePlanClub={clubData}
			/>
		</div>
	);
}
